import React from 'react';
import { Alert, Box, Paper, Typography } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { AirLineLogo } from 'features/flightsExplorer/components/AirlineLogo';
import { CarrierChannelSummary } from '../CarrierChannelSummary';
import { MerchantRulesTable } from '../MerchantRulesTable';
import { isMerchantRuleExpired } from '../../helpers/authorizationGuide';
import { CarrierReferenceItem } from '../../helpers/carrierReference';

interface CarrierConditionsPanelProps {
	carrier: CarrierReferenceItem | null;
	onCopied?: (message: string) => void;
}

export const CarrierConditionsPanel = ({ carrier, onCopied }: CarrierConditionsPanelProps): JSX.Element => {
	if (!carrier) {
		return (
			<Paper
				variant="outlined"
				sx={{
					p: 4,
					borderRadius: 2,
					minHeight: 320,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<Typography variant="body1" color="text.secondary" textAlign="center">
					Seleccioná una aerolínea para ver canal de autorización, códigos de comercio y planes de cuotas.
				</Typography>
			</Paper>
		);
	}

	const { rules, summaryType } = carrier;
	const hasExpiredRules = rules.some((r) => isMerchantRuleExpired(r.validUntil));

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 320 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
				<AirLineLogo code={carrier.code} name={carrier.name} showName />
				<Typography variant="caption" color="text.secondary" fontWeight="bold">
					{carrier.code}
				</Typography>
			</Box>

			<CarrierChannelSummary carrierName={carrier.name} summaryType={summaryType} onCopied={onCopied} />

			<Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
					<StorefrontIcon color="primary" fontSize="small" />
					<Typography variant="subtitle2" fontWeight="bold">
						Códigos de comercio y cuotas
					</Typography>
				</Box>

				{hasExpiredRules && (
					<Alert severity="warning" sx={{ mb: 2 }}>
						Hay filas con validez vencida. Verificá con supervisión antes de autorizar.
					</Alert>
				)}

				{rules.length > 0 ? (
					<MerchantRulesTable rules={rules} showNotes />
				) : (
					<Alert severity="warning">No hay reglas configuradas para esta aerolínea.</Alert>
				)}
			</Paper>

			<Typography variant="caption" color="text.secondary">
				Referencia operativa. Los datos se actualizan en el sistema junto al flujo de autorización C2A.
			</Typography>
		</Box>
	);
};
